// Affiliate click-tracking hook.
// On every route change, inspects the URL for ?aff=<code> (and any utm_*
// params). If present, fires a tracking call to the backend so it can set
// the vy9_aff attribution cookie, then strips ?aff= from the URL while
// preserving any other meaningful query params (e.g. ?ref=... referral).
//
// Silent-fail by design — affiliate tracking must never block UX.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackAffiliateClick } from "@/lib/affiliateApi";

export default function useAffiliateTracking() {
  const location = useLocation();
  const search = location.search;

  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const affCode = params.get("aff");
    if (!affCode) return;

    // Collect the aff code + any utm_* params to forward to the tracker.
    const payload = { aff: affCode };
    for (const [k, v] of params.entries()) {
      if (k.startsWith("utm_")) payload[k] = v;
    }
    trackAffiliateClick(payload);

    // Clean the URL so refresh/share doesn't re-fire the tracker and so
    // the URL is tidy. Keep all params that aren't aff/utm_*.
    const cleanParams = new URLSearchParams();
    for (const [k, v] of params.entries()) {
      if (k === "aff") continue;
      if (k.startsWith("utm_")) continue;
      cleanParams.set(k, v);
    }
    const cleanQs = cleanParams.toString();
    const cleanUrl = location.pathname + (cleanQs ? `?${cleanQs}` : "") + (location.hash || "");
    window.history.replaceState({}, "", cleanUrl);
  }, [search, location.pathname, location.hash]);
}
